package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.auth.AuthUserRole
import com.royceecoleman.bill.auth.AuthenticatedSession
import com.royceecoleman.bill.auth.createUserAccount
import com.royceecoleman.bill.auth.creatableRoles
import com.royceecoleman.bill.auth.displayName
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

@Page
@Composable
fun accountManagementPage() {
    GuardedAdminPage(
        checkingMessage = "Loading account management for your authenticated role.",
        redirectingToLoginMessage = "You need to sign in before managing user accounts.",
        redirectingToMapMessage = "Customer accounts cannot manage other users.",
    ) { currentSession ->
        AccountManagementContent(
            currentSession = currentSession,
        )
    }
}

@Composable
private fun AccountManagementContent(currentSession: AuthenticatedSession) {
    val coroutineScope = rememberCoroutineScope()
    val availableRoles = currentSession.role.creatableRoles()
    var username by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var selectedRole by remember(currentSession.role) { mutableStateOf(availableRoles.first()) }
    var feedbackMessage by remember { mutableStateOf<String?>(null) }
    var feedbackIsError by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }

    CardPage(
        title = "Account Management",
        supportingText =
            "${currentSession.role.displayName()} users can create ${availableRoles.joinToString { it.displayName() }} accounts.",
    ) {
        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("gap", "1rem")
                    property("margin-top", "1.5rem")
                }
            },
        ) {
            P(
                attrs = {
                    style {
                        property("margin", "0")
                        property("color", "#334155")
                        property("line-height", "1.6")
                    }
                },
            ) {
                Text("Create a new user account and persist it to the configured database.")
            }

            if (feedbackMessage != null) {
                Div(
                    attrs = {
                        style {
                            property("padding", "0.85rem 1rem")
                            property("border-radius", "0.75rem")
                            property("background", if (feedbackIsError) "#fee2e2" else "#dcfce7")
                            property("color", if (feedbackIsError) "#b91c1c" else "#166534")
                        }
                    },
                ) {
                    Text(checkNotNull(feedbackMessage))
                }
            }

            Form(
                attrs = {
                    onSubmit { event ->
                        event.preventDefault()

                        if (isSubmitting) {
                            return@onSubmit
                        }

                        coroutineScope.launch {
                            isSubmitting = true
                            feedbackMessage = null

                            try {
                                val result =
                                    createUserAccount(
                                        username = username.trim(),
                                        password = password,
                                        role = selectedRole,
                                    )

                                feedbackIsError = !result.isSuccess
                                feedbackMessage =
                                    if (result.isSuccess) {
                                        val createdUser = checkNotNull(result.createdUser)
                                        username = ""
                                        password = ""
                                        selectedRole = availableRoles.first()
                                        "Created ${createdUser.role.displayName()} account for ${createdUser.username}."
                                    } else {
                                        result.message
                                    }
                            } catch (error: Throwable) {
                                if (error is CancellationException) {
                                    throw error
                                }

                                feedbackIsError = true
                                feedbackMessage = error.message?.takeIf(String::isNotBlank) ?: "Unable to reach the server."
                            } finally {
                                isSubmitting = false
                            }
                        }
                    }
                    style {
                        property("display", "grid")
                        property("gap", "1rem")
                    }
                },
            ) {
                Div {
                    Label(
                        forId = "new-username",
                        attrs = {
                            style {
                                property("display", "block")
                                property("font-weight", "600")
                                property("margin-bottom", "0.45rem")
                            }
                        },
                    ) {
                        Text("Username")
                    }
                    Input(
                        type = InputType.Text,
                        attrs = {
                            id("new-username")
                            value(username)
                            onInput { username = it.value }
                            attr("autocomplete", "username")
                            attr("required", "true")
                            style {
                                property("width", "100%")
                                property("padding", "0.8rem 0.95rem")
                                property("border", "1px solid #cbd5e1")
                                property("border-radius", "0.75rem")
                                property("font-size", "1rem")
                                property("box-sizing", "border-box")
                            }
                        },
                    )
                }

                Div {
                    Label(
                        forId = "new-password",
                        attrs = {
                            style {
                                property("display", "block")
                                property("font-weight", "600")
                                property("margin-bottom", "0.45rem")
                            }
                        },
                    ) {
                        Text("Password")
                    }
                    Input(
                        type = InputType.Password,
                        attrs = {
                            id("new-password")
                            value(password)
                            onInput { password = it.value }
                            attr("autocomplete", "new-password")
                            attr("required", "true")
                            style {
                                property("width", "100%")
                                property("padding", "0.8rem 0.95rem")
                                property("border", "1px solid #cbd5e1")
                                property("border-radius", "0.75rem")
                                property("font-size", "1rem")
                                property("box-sizing", "border-box")
                            }
                        },
                    )
                }

                Div {
                    Label(
                        forId = "new-role",
                        attrs = {
                            style {
                                property("display", "block")
                                property("font-weight", "600")
                                property("margin-bottom", "0.45rem")
                            }
                        },
                    ) {
                        Text("Role")
                    }
                    Select(
                        attrs = {
                            id("new-role")
                            onChange { event ->
                                selectedRole = AuthUserRole.valueOf(checkNotNull(event.value))
                            }
                            style {
                                property("width", "100%")
                                property("padding", "0.8rem 0.95rem")
                                property("border", "1px solid #cbd5e1")
                                property("border-radius", "0.75rem")
                                property("font-size", "1rem")
                                property("background", "#ffffff")
                                property("box-sizing", "border-box")
                            }
                        },
                    ) {
                        availableRoles.forEach { role ->
                            Option(
                                value = role.name,
                                attrs = {
                                    if (role == selectedRole) {
                                        attr("selected", "selected")
                                    }
                                },
                            ) {
                                Text(role.displayName())
                            }
                        }
                    }
                }

                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("gap", "0.75rem")
                            property("flex-wrap", "wrap")
                            property("margin-top", "0.5rem")
                        }
                    },
                ) {
                    Button(
                        attrs = {
                            attr("type", "submit")
                            style {
                                property("padding", "0.9rem 1rem")
                                property("border", "0")
                                property("border-radius", "0.8rem")
                                property("background", "#2563eb")
                                property("color", "#ffffff")
                                property("font-size", "1rem")
                                property("font-weight", "700")
                                property("cursor", "pointer")
                            }
                        },
                    ) {
                        Text(if (isSubmitting) "Creating..." else "Create user")
                    }

                    Button(
                        attrs = {
                            attr("type", "button")
                            onClick { window.location.href = "/dashboard" }
                            style {
                                property("padding", "0.9rem 1rem")
                                property("border", "1px solid #cbd5e1")
                                property("border-radius", "0.8rem")
                                property("background", "#ffffff")
                                property("color", "#0f172a")
                                property("font-size", "1rem")
                                property("font-weight", "600")
                                property("cursor", "pointer")
                            }
                        },
                    ) {
                        Text("Back to dashboard")
                    }
                }
            }
        }
    }
}
